/* v3.2.14 · Job-scoped Deliverables Exchange.
   Distinct from the older /deliverables page (which handles the
   ETAP-lite auto-generated deliverables for the client's own projects).
   THIS page lists every awarded marketplace job the caller is part of
   (client owner OR the winning PE) and lets both sides upload / download
   documents in a shared folder. Uploads are auto-versioned (Rev1, Rev2, …)
   and produce Attention-Feed notifications for the other party. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Folder, FolderOpen, DownloadSimple, Upload, Trash, ArrowClockwise,
  CaretDown, CaretRight, FileText, Handshake, ClockCounterClockwise,
} from "@phosphor-icons/react";

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function JobDeliverablesPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState(new Set());
  const [filesByJob, setFilesByJob] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const inputRefs = useRef({});

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/job-deliverables/projects");
      setProjects(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load projects");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadFiles = async (jobId) => {
    try {
      const { data } = await api.get(`/jobs/${jobId}/deliverables`);
      setFilesByJob((m) => ({ ...m, [jobId]: data || [] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load files");
    }
  };

  const toggle = async (jobId) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else { next.add(jobId); if (!filesByJob[jobId]) loadFiles(jobId); }
      return next;
    });
  };

  const upload = async (jobId, file) => {
    if (!file) return;
    setUploadingId(jobId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/jobs/${jobId}/deliverables/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${file.name}".`);
      await loadFiles(jobId);
      await loadProjects();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingId(null);
      if (inputRefs.current[jobId]) inputRefs.current[jobId].value = "";
    }
  };

  const remove = async (fileId, jobId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/job-deliverables/${fileId}`);
      await loadFiles(jobId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0F1218] text-white">
      <Topbar />
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="job-deliverables-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-4xl font-bold flex items-center gap-2">
              <Handshake size={28} weight="fill" className="text-[#00E5FF]" /> Deliverables Exchange
            </h1>
            <div className="text-xs text-gray-400 font-mono mt-1">
              Shared folder for each awarded project · files are auto-versioned (Rev1, Rev2, …) · 50 MB max
            </div>
          </div>
          <button onClick={loadProjects}
            className="text-xs text-gray-400 hover:text-white font-mono inline-flex items-center gap-1"
            data-testid="job-deliverables-refresh">
            <ArrowClockwise size={12} /> refresh
          </button>
        </div>

        {loading && <div className="text-sm text-gray-500 font-mono">Loading…</div>}
        {!loading && projects.length === 0 && (
          <div className="border border-white/10 bg-white/5 p-8 text-center text-sm font-mono text-gray-400"
               data-testid="job-deliverables-empty">
            <Folder size={22} className="mx-auto mb-2 text-gray-500" />
            {user.role === "pe"
              ? "No awarded jobs yet. Once a client accepts your proposal, your shared folder will appear here."
              : "No jobs in progress. Accept a proposal to open a deliverables folder with the awarded engineer."}
          </div>
        )}

        {!loading && projects.length > 0 && (
          <div className="space-y-3">
            {projects.map((p) => {
              const isOpen = openIds.has(p.id);
              const files = filesByJob[p.id] || [];
              return (
                <div key={p.id}
                  className="border border-white/10 bg-[#12151C]"
                  data-testid={`deliverables-folder-${p.id}`}>
                  <button onClick={() => toggle(p.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition"
                    data-testid={`deliverables-folder-toggle-${p.id}`}>
                    {isOpen ? <CaretDown size={14} className="text-gray-500" /> : <CaretRight size={14} className="text-gray-500" />}
                    {isOpen ? <FolderOpen size={18} weight="fill" className="text-[#FFCC00]" /> : <Folder size={18} weight="fill" className="text-[#FFCC00]/70" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold truncate">{p.title}</div>
                      <div className="text-[10px] font-mono text-gray-500">
                        {user.role === "client" ? `with ${p.awarded_pe_name || "PE"}` : `for ${p.client_company || p.client_name || "client"}`}
                        {p.updated_at && <> · updated {fmtWhen(p.updated_at)}</>}
                      </div>
                    </div>
                    <div className="text-[10px] font-mono text-gray-400 inline-flex items-center gap-1">
                      <FileText size={11} /> {p.file_count || 0} file{p.file_count === 1 ? "" : "s"}
                    </div>
                    <Link to={`/jobs/${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] font-mono text-[#00E5FF] hover:underline ml-3">
                      open job →
                    </Link>
                  </button>

                  {isOpen && (
                    <div className="border-t border-white/10 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <input type="file"
                          ref={(el) => { inputRefs.current[p.id] = el; }}
                          onChange={(e) => upload(p.id, e.target.files?.[0])}
                          className="hidden"
                          data-testid={`deliverables-file-input-${p.id}`} />
                        <button onClick={() => inputRefs.current[p.id]?.click()}
                          disabled={uploadingId === p.id}
                          className="btn-primary text-xs py-1.5 px-3 disabled:opacity-40 inline-flex items-center gap-1"
                          data-testid={`deliverables-upload-btn-${p.id}`}>
                          <Upload size={12} weight="fill" />
                          {uploadingId === p.id ? "Uploading…" : "Upload file"}
                        </button>
                        <span className="text-[10px] font-mono text-gray-500">
                          Same filename bumps a revision (Rev2, Rev3, …) · max 50 MB
                        </span>
                      </div>
                      {files.length === 0 ? (
                        <div className="text-xs text-gray-500 font-mono italic border border-white/5 p-3">
                          No files yet — be the first to upload.
                        </div>
                      ) : (
                        <div className="border border-white/10">
                          <div className="grid grid-cols-12 bg-black/40 text-[10px] font-mono uppercase tracking-wider text-gray-500 px-3 py-1.5">
                            <div className="col-span-6">File</div>
                            <div className="col-span-2">Rev</div>
                            <div className="col-span-2">Size</div>
                            <div className="col-span-2 text-right">Actions</div>
                          </div>
                          {files.map((f) => (
                            <div key={f.file_id}
                              className="grid grid-cols-12 items-center px-3 py-2 border-t border-white/5 text-xs"
                              data-testid={`deliverables-file-${f.file_id}`}>
                              <div className="col-span-6 truncate">
                                <div className="text-white font-mono truncate" title={f.original_name}>{f.original_name}</div>
                                <div className="text-[10px] text-gray-500">
                                  by {f.uploaded_by_name || "—"} · {fmtWhen(f.uploaded_at)}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-mono ${f.revision > 1 ? "border-[#FFCC00]/40 text-[#FFCC00] bg-[#FFCC00]/10" : "border-white/10 text-gray-400"}`}>
                                  <ClockCounterClockwise size={9} /> Rev{f.revision}
                                </span>
                              </div>
                              <div className="col-span-2 text-gray-400 font-mono">{fmtBytes(f.size)}</div>
                              <div className="col-span-2 flex items-center justify-end gap-2">
                                <a href={`${getBackendUrl()}/api/job-deliverables/${f.file_id}`}
                                  target="_blank" rel="noreferrer"
                                  className="text-[#00E5FF] hover:underline inline-flex items-center gap-1"
                                  data-testid={`deliverables-download-${f.file_id}`}>
                                  <DownloadSimple size={12} weight="fill" />
                                </a>
                                {f.uploaded_by === user.id && (
                                  <button onClick={() => remove(f.file_id, p.id)}
                                    className="text-[#FF3B30] hover:text-red-400"
                                    data-testid={`deliverables-remove-${f.file_id}`}>
                                    <Trash size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
